import React from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../../constants/Colors";
import StepWrapper from "./StepWrapper";
import { CreateFlatStore, MIN_IMAGES } from "../../hooks/useCreateFlatStore";
import ImageUploader, { UploadedImage } from "../ImageUploader";

interface Props {
  store: CreateFlatStore;
  onAuthRequired: () => void;
}

const ImagesStep: React.FC<Props> = ({ store, onAuthRequired }) => {
  const { t } = useTranslation();
  const { textNeutralMuted, accentColor } = useColors();

  const uploadedCount = store.images.length;
  const hasMinimum = uploadedCount >= MIN_IMAGES;

  const flatIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="44"
      height="44"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mx-auto mb-3"
      style={{ color: textNeutralMuted }}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>
  );

  return (
    <StepWrapper
      title={
        t("flats_tab.add_flat.flat_images_title") || "Add photos of your flat"
      }
      subtitle={`${t("minimum") || "Minimum"} ${MIN_IMAGES} ${
        t("photos") || "photos"
      }`}
    >
      <div>
        {/* Upload counter */}
        <div className="flex items-center justify-center gap-2 mb-5">
          <div
            className="px-3 py-1 rounded-full text-xs font-medium"
            style={{
              backgroundColor: hasMinimum
                ? "rgba(91, 61, 246, 0.08)"
                : "rgba(2, 2, 16, 0.06)",
              color: hasMinimum ? accentColor : textNeutralMuted,
            }}
          >
            {uploadedCount} {t("uploaded") || "uploaded"}
          </div>
        </div>

        <ImageUploader
          images={store.images as UploadedImage[]}
          onImagesChange={(updater) => {
            if (typeof updater === "function") {
              store.setImages(updater);
            } else {
              store.setImages(() => updater);
            }
          }}
          onRemoveImage={store.removeImage}
          kind="flat"
          minImages={MIN_IMAGES}
          maxImages={9}
          onAuthRequired={onAuthRequired}
          icon={flatIcon}
          showNumberBadge={true}
        />
      </div>
    </StepWrapper>
  );
};

export default ImagesStep;
